/* iter55g28 — Stacks Search bar for the customer bookstore landing.
 *
 * A single search box that queries `/api/bookstore/search` and pops an
 * ephemeral result panel below the hero. Behaviour depends on what
 * matched:
 *   • ISBN or exact-single title → renders a single centered thumbnail
 *     the customer can click to open the Book Detail modal.
 *   • Author / genre / deals / mixed → renders a SpinCarousel of the
 *     matched books (like the storefront's other carousels).
 *   • No hits → a soft empty state with retry copy.
 *
 * The panel is dismissable; clearing the query hides it entirely.
 * All results respect the bookstore visibility rules (hidden genres +
 * hidden books excluded server-side).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SpinCarousel from '@/components/bookstore/SpinCarousel';
import BookCard from '@/components/bookstore/BookCard';
import { resolveCoverUrl, trackStorefrontEvent } from '@/lib/bookstoreApi';
import { P, FONTS } from '@/components/landing/scriptorium/palette';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const SUGGESTIONS = ['deals', 'romance', 'thriller', 'literary fiction'];

function BigThumbnail({ book, onOpen }) {
  const url = resolveCoverUrl(book.cover_front_url || book.cover_thumb_url);
  return (
    <div className="flex flex-col items-center gap-3" data-testid="stacks-search-thumb">
      <button
        type="button"
        onClick={() => onOpen(book)}
        className="group relative overflow-hidden rounded-md shadow-2xl transition-transform hover:scale-[1.02]"
        style={{ border: `1px solid ${P.gold}`, backgroundColor: P.paperDeep }}
        data-testid="stacks-search-thumb-btn"
      >
        {url
          ? <img src={url} alt={book.title}
                 className="h-72 w-48 object-cover md:h-96 md:w-64" />
          : (
            <div className="flex h-72 w-48 items-center justify-center md:h-96 md:w-64"
                 style={{ color: P.inkSoft }}>
              <BookOpen className="h-10 w-10" />
            </div>
          )}
      </button>
      <div className="text-center">
        <div className="text-lg font-semibold" style={{ color: P.ink, fontFamily: FONTS.display }}>
          {book.title}
        </div>
        <div className="text-[13px]" style={{ color: P.inkSoft }}>{book.author}</div>
      </div>
    </div>
  );
}

export default function StacksSearchBar({ onOpenBook }) {
  const [q, setQ]           = useState('');
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (query) => {
    const clean = (query || '').trim();
    if (clean.length < 2) {
      setData(null);
      setError('');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await axios.get(`${API}/bookstore/search`, { params: { q: clean } });
      setData(r.data);
      const matchedGenre = r.data?.genres?.[0]?.slug || r.data?.books?.[0]?.genre_slug;
      const matchedBook = r.data?.books?.[0]?.id;
      if (matchedGenre || matchedBook) {
        trackStorefrontEvent({ event_type: 'search_interest', genre_slug: matchedGenre, book_id: matchedBook });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (e?.message || 'Search failed.');
      setError(msg);
      setData(null);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  // Debounce keystrokes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  const clear = () => { setQ(''); setData(null); setError(''); };

  const showPanel = Boolean(data || busy || error);
  const displayHint = data?.display_hint;
  const books = data?.books || [];

  return (
    <div className="relative z-10 mx-auto mt-6 max-w-3xl px-6" data-testid="stacks-search-bar-wrap">
      <div
        className="flex items-center gap-2 rounded-md px-3 py-2"
        style={{ border: `1px solid ${P.gold}`, backgroundColor: P.paperDeep }}
      >
        <Search className="h-4 w-4" style={{ color: P.oxblood }} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, author, genre, ISBN, or 'deals'…"
          data-testid="stacks-search-input"
          className="flex-1 bg-transparent outline-none placeholder:text-[13px]"
          style={{ color: P.ink }}
          aria-label="Search the bookstore"
        />
        {busy && <Loader2 className="h-4 w-4 animate-spin" style={{ color: P.inkSoft }} />}
        {q && !busy && (
          <button
            type="button"
            onClick={clear}
            data-testid="stacks-search-clear"
            className="rounded p-1 hover:bg-black/5"
            style={{ color: P.inkSoft }}
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {!showPanel && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px] uppercase tracking-[0.2em]"
             style={{ color: P.inkSoft, fontFamily: FONTS.type }}>
          <span>Try:</span>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setQ(s)}
              className="rounded-sm px-2 py-0.5 hover:opacity-80"
              style={{ border: `1px solid ${P.gold}`, color: P.oxblood }}
              data-testid={`stacks-search-suggest-${s.replace(/\s+/g, '-')}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {showPanel && (
        <div
          className="mt-4 rounded-md p-5"
          style={{ border: `1px solid ${P.gold}`, backgroundColor: P.paper }}
          data-testid="stacks-search-panel"
        >
          {error && (
            <div className="text-[13px]" style={{ color: P.oxblood }}
                 data-testid="stacks-search-error">
              {error}
            </div>
          )}
          {!error && data && books.length === 0 && (
            <div className="text-center text-[13px]" style={{ color: P.inkSoft }}
                 data-testid="stacks-search-empty">
              No books matched <em>&ldquo;{data.q}&rdquo;</em>. Try a different title,
              author, genre, or the keyword <strong>deals</strong>.
            </div>
          )}
          {!error && data && books.length > 0 && (
            <>
              <div className="mb-3 text-[10.5px] uppercase tracking-[0.28em]"
                   style={{ color: P.oxblood, fontFamily: FONTS.type }}>
                {data.match_type === 'deals'
                  ? 'Current deals'
                  : data.match_type === 'isbn'
                    ? 'ISBN match'
                    : data.match_type === 'title'
                      ? 'Title match'
                      : data.match_type === 'author'
                        ? `Books by ${books[0]?.author || ''}`
                        : data.match_type === 'genre'
                          ? 'Genre match'
                          : `Matches for "${data.q}"`}
              </div>
              {displayHint === 'thumbnail'
                ? <BigThumbnail book={books[0]} onOpen={onOpenBook} />
                : (
                  <SpinCarousel testid="stacks-search-carousel">
                    {books.map((b) => (
                      <BookCard key={b.id} book={b} onClick={onOpenBook} />
                    ))}
                  </SpinCarousel>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
